import { Label, Radio } from "@headlessui/react";
import type { Nullable } from "@msuite/utils/types";
import { cva } from "class-variance-authority";
import { motion } from "motion/react";
import React, { Fragment } from "react";
import { cn } from "../utils";

/** Props Interface */
interface SegmentedControlItemProps<T extends string> {
	value: T;
	label: string;
	cyId: string;
	parentId: string;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	description?: string;
	action?: React.ReactNode;
	orientation?: Nullable<"horizontal" | "vertical">;
	isDisabled?: boolean;
}

export function SegmentedControlItem<T extends string>({
	value,
	label,
	cyId,
	parentId,
	orientation = "horizontal",
	leftIcon,
	rightIcon,
	description,
	action,
	isDisabled,
}: SegmentedControlItemProps<T>) {
	/** Classes */
	const segmentedControlItemClasses = segmentedControlItem({
		orientation,
	});

	/** Computed */
	// Vertical only: beneath a horizontal item there is no room for a second line, so the
	// description is dropped rather than squeezed in.
	const hasDescription = orientation === "vertical" && Boolean(description);

	/** Render */
	return (
		<Radio
			as={Fragment}
			value={value}
			disabled={isDisabled}
			data-cy={cyId}
		>
			{({ checked }) => (
				<div className={cn("group", segmentedControlItemClasses)}>
					{checked && (
						<motion.div
							layoutId={`${parentId}-segmented-control-item-active`}
							className={cn(
								"card",
								"segmented-control-floater",
								"card-bg inset-0 absolute",
								orientation === "horizontal" ? "rounded-p-0.5" : "rounded-p-0.5",
							)}
							transition={{ duration: 0.2, ease: "circInOut" }}
						/>
					)}
					<div className="h-stack relative z-1 flex-1 min-w-0">
						{leftIcon && leftIcon}
						{hasDescription ? (
							<div className="v-stack gap-0.5 min-w-0">
								<Label className="font-medium truncate">{label}</Label>
								<p className="opacity-50 text-xs">{description}</p>
							</div>
						) : (
							<Label className="font-medium">{label}</Label>
						)}
						{rightIcon && rightIcon}
					</div>
					{action && (
						// Outside the label and shielded from the radio's click handler: using the
						// action must not also change the selection.
						<div
							onClick={(event) => event.stopPropagation()}
							className="relative z-1 shrink-0"
						>
							{action}
						</div>
					)}
				</div>
			)}
		</Radio>
	);
}

const segmentedControlItem = cva(
	// The focus ring targets the floater by class, not by child position: with the optional
	// action slot the content div stopped being the last child, and `*:not-last` drew a second
	// ring around it.
	[
		"h-stack relative cursor-default [&:focus>.segmented-control-floater]:input-focused",
		"data-disabled:opacity-40",
	],
	{
		variants: {
			orientation: {
				horizontal: ["px-4 py-1.5 text-xs"],
				vertical: ["px-3 py-2"],
			},
		},
	},
);
